import { resources } from "config";
import object_template_attribute from "config/resources/object_template_attribute";
import plugin_action from "config/resources/plugin_action";
import resource_wrapper from "config/resources/resource_wrapper";
import role_permission from "config/resources/role_permission";
import user_git_public_key from "config/resources/user_git_public_key";
import { Record } from "graphql/generated/graphql";
import {
    ActivityConfig,
    FeConfigType,
    RecordClass,
    RelationshipConfig,
    ResourceConfig,
} from "./config";

export const getIsReadonly = (resourceConfig: any, resourceData: any) =>
    Boolean(
        !resourceConfig?.ignoreIsReadonly &&
            (resourceData?.is_readonly ||
                resourceData?.is_project_readonly ||
                resourceData?.is_bundle_readonly)
    );
export const getIsBundleContent = (resourceData: any) =>
    Boolean(resourceData?.bundle_id && resourceData?.is_bundle_content);

const writeOnlyFieldsByResource: Partial<Record<FeConfigType, string[]>> = {
    bundle_repository: ["api_key", "write_key"],
    sync_config: ["own_private_key"],
    vault_config: ["token", "password", "client_key"],
    devolutions_config: ["app_secret"],
    ldap_config: ["password", "client_key"],
};

export const getWriteOnlyFieldsWithData = (resourceName: FeConfigType, resourceData?: Record) => {
    const writeOnlyFieldsWithData: Record<string, boolean> = {};
    for (const fieldName of writeOnlyFieldsByResource[resourceName] ?? []) {
        const hasValueFieldName = `has_${fieldName}`;
        if (resourceData?.hasOwnProperty(hasValueFieldName)) {
            writeOnlyFieldsWithData[fieldName] = resourceData[hasValueFieldName];
        }
    }
    return writeOnlyFieldsWithData;
};

/**
 * A field is write-only (e.g. a secret / password value) when it uses the
 * `writeOnlyInput` component: it is empty on load and its stored value is masked
 * on read. Such a field must NEVER be seeded with a schema/DB default value — a
 * seeded default would be shipped in the mutation on any save that leaves the
 * field untouched and silently overwrite the real stored secret. This is keyed
 * off the field component itself (not only the `writeOnlyFieldsByResource`
 * allowlist, which drives the has-value display indicator) so a newly-added
 * write-only resource cannot forget to register and reintroduce the clobber.
 */
export const isWriteOnlyField = (
    field: { component?: string; name: string },
    writeOnlyFieldsWithData: { [key: string]: boolean }
): boolean => field.component === "writeOnlyInput" || field.name in writeOnlyFieldsWithData;

/** Coerce null/undefined optional text fields to "" so textarea inputs match initial form state. */
export const normalizeNullableStringFields = <T extends Record<string, unknown>>(
    data: T | undefined | null,
    fieldNames: string[]
): T => {
    if (!data) {
        return {} as T;
    }
    const normalized = { ...data } as Record<string, unknown>;
    for (const fieldName of fieldNames) {
        if (normalized[fieldName] == null) {
            normalized[fieldName] = "";
        }
    }
    return normalized as T;
};

export const getIsDisabled = (resourceConfig: any, resourceData: any) =>
    Boolean(
        getIsReadonly(resourceConfig, resourceData) ||
            resourceData?.deleted_at ||
            resourceData?.role_id_role?.deleted_at ||
            resourceData?.role_id_role?.is_readonly ||
            resourceData?.resource_id_resource?.deleted_at ||
            resourceData?.resource_id_resource?.is_readonly
    );

export const getRecordConfig = (
    recordTypeName: FeConfigType
): ActivityConfig | ResourceConfig | RelationshipConfig => {
    return (
        resources?.filter((recordConfig) => recordConfig?.resourceName === recordTypeName)[0] ?? {}
    );
};

export const getRecordClass = (recordTypeName: FeConfigType): RecordClass => {
    return getRecordConfig(recordTypeName)?.resourceClass;
};

const getRecordTypeName = (recordTypeName: FeConfigType) =>
    getRecordConfig(recordTypeName)?.resourceName;

export const isSubrecordOrRelationshipType = (recordTypeName: FeConfigType) =>
    getRecordTypeName(recordTypeName) === role_permission.resourceName ||
    getRecordTypeName(recordTypeName) === plugin_action.resourceName ||
    getRecordTypeName(recordTypeName) === resource_wrapper.resourceName ||
    getRecordTypeName(recordTypeName) === user_git_public_key.resourceName ||
    getRecordTypeName(recordTypeName) === object_template_attribute.resourceName;
